import { forwardRef, useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
  type ToggleButtonProps,
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import EmptyLedgerIcon from '@mui/icons-material/ReceiptLong';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import cpeHistoryService from '../services/cpeHistoryService';
import creditImportService from '../services/creditImportService';
import type { ImportDoneState } from './CpeHistoryImport';
import LicenseRequirementsSection from '../components/compliance/LicenseRequirementsSection';
import type { CreditRecord, EducationCategory } from '../services/cpeHistoryService';
import type { LicenseFulfillmentDto } from '../services/licenseComplianceService';
import { buildLicensePresets, formatPeriodDate, formatPeriodRange } from '../utils/reportingPeriods';
import { useUserContext } from '../hooks/useUserContext';
import { generateCpeReport } from '../utils/cpeReportPdf';
import ThirdPartyCreditDialog from '../components/ThirdPartyCreditDialog';
import { CreditImportPanel } from '../components/CreditImportPanel';
import { useCreditImports } from '../hooks/useCreditImports';
import { externalSourceLabel } from '../utils/externalSources';

function defaultEnd(): string {
  return new Date().toISOString().slice(0, 10);
}

function thisYearStart(): string {
  return `${new Date().getFullYear()}-01-01`;
}

function lastYearStart(): string {
  return `${new Date().getFullYear() - 1}-01-01`;
}

function twoYearsAgoStart(): string {
  return `${new Date().getFullYear() - 2}-01-01`;
}

function lastYearEnd(): string {
  return `${new Date().getFullYear() - 1}-12-31`;
}

type GroupedCreditRow = {
  key: string;
  base: CreditRecord;
  totalCredits: number;
  entries: Array<{
    id: string;
    creditsEarned: number;
    category?: string;
  }>;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// Credits are tenths-valued, but summing them in IEEE 754 floats accumulates
// noise (0.1 + 0.2 = 0.30000000000000004) — round sums back to one decimal.
function roundTenths(value: number): number {
  return Math.round(value * 10) / 10;
}

function hasUsableGuid(value?: string): value is string {
  return Boolean(value && value !== EMPTY_GUID);
}

function groupCreditsForDisplay(records: CreditRecord[]): GroupedCreditRow[] {
  const groups = new Map<string, CreditRecord[]>();

  for (const record of records) {
    const groupKey = record.source === 'External'
      ? `external:${hasUsableGuid(record.creditGroupId) ? record.creditGroupId : record.id}`
      : `platform:${hasUsableGuid(record.registrationId) ? record.registrationId : record.id}`;

    const current = groups.get(groupKey);
    if (current) {
      current.push(record);
    } else {
      groups.set(groupKey, [record]);
    }
  }

  const groupedRecords: GroupedCreditRow[] = [];

  for (const [key, recordsInGroup] of groups.entries()) {
    const sortedByDateDesc = [...recordsInGroup].sort(
      (a, b) => new Date(b.dateEarned).getTime() - new Date(a.dateEarned).getTime(),
    );

    const base = sortedByDateDesc[0];
    groupedRecords.push({
      key,
      base,
      totalCredits: roundTenths(recordsInGroup.reduce((sum, current) => sum + current.creditsEarned, 0)),
      entries: recordsInGroup.map((entry) => ({
        id: entry.id,
        creditsEarned: entry.creditsEarned,
        category: entry.category,
      })),
    });
  }

  return groupedRecords.sort(
    (a, b) => new Date(b.base.dateEarned).getTime() - new Date(a.base.dateEarned).getTime(),
  );
}

/**
 * ToggleButton with a tooltip that still works inside a ToggleButtonGroup.
 * The group injects selection props into its direct children via cloneElement,
 * so they must land here (and be spread onto the button) rather than on a
 * bare Tooltip, which would swallow them.
 */
const TooltipToggleButton = forwardRef<HTMLButtonElement, ToggleButtonProps & { tooltip: string }>(
  function TooltipToggleButton({ tooltip, ...props }, ref) {
    return (
      <Tooltip title={tooltip}>
        <ToggleButton ref={ref} {...props} />
      </Tooltip>
    );
  },
);

export default function CpeHistory() {
  const { clerkUser } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Arriving back from the spreadsheet import: a one-shot success snackbar with Undo.
  const [importDone, setImportDone] = useState<ImportDoneState['importedBatch'] | null>(
    () => (location.state as ImportDoneState | null)?.importedBatch ?? null,
  );
  const [undoingImport, setUndoingImport] = useState(false);
  const [importUndoError, setImportUndoError] = useState<string | null>(null);
  useEffect(() => {
    if ((location.state as ImportDoneState | null)?.importedBatch) {
      // Clear the navigation state so a refresh doesn't re-offer Undo for a stale batch.
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [credits, setCredits] = useState<CreditRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [startDate, setStartDate] = useState(thisYearStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [generating, setGenerating] = useState(false);
  const [pdfSnackOpen, setPdfSnackOpen] = useState(false);
  const [certificateSnackOpen, setCertificateSnackOpen] = useState(false);
  // Calendar presets ('year' | 'twoYear' | 'pastTwo' | 'all') or a
  // license-period preset ('license:<licenseId>'); null = custom range.
  const [activePreset, setActivePreset] = useState<string | null>('year');
  // null until the fulfillment fetch settles — license presets and the generic
  // two-year fallbacks only render once we know which applies (buttons are
  // added, never removed, so an active selection can't be invalidated).
  const [licenses, setLicenses] = useState<LicenseFulfillmentDto[] | null>(null);

  // Third-party credit state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<CreditRecord | undefined>();
  const [deleteRecord, setDeleteRecord] = useState<CreditRecord | undefined>();
  const [deleteError, setDeleteError] = useState(false);
  const [categories, setCategories] = useState<EducationCategory[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Bumped whenever credits change so LicenseRequirementsSection refetches
  // fulfillment totals in place, without a page refresh.
  const [requirementsRefresh, setRequirementsRefresh] = useState(0);
  // Category filter driven by the summary donut / legend; cleared whenever
  // the period changes (a slice from one period has no meaning in another).
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const toggleCategoryFilter = (category: string) =>
    setCategoryFilter((prev) => (prev === category ? null : category));

  const loadCredits = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    cpeHistoryService.getUserCredits()
      .then((data) => {
        setCredits(data);
        setLoading(false);
      })
      .catch(() => {
        setLoadError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadCredits();
    cpeHistoryService.getEducationCategories().then(setCategories).catch(() => {});
  }, [loadCredits]);

  // External credit imports (PICPA/Aptify today, CPAx later) — one controller per
  // available source; an import only ever runs on the user's explicit yes. A
  // successful import refreshes the ledger and the requirement totals.
  const { imports: creditImports } = useCreditImports((result, status) => {
    const imported = result.created + result.updated;
    setSuccessMessage(
      result.fetched === 0
        ? `No ${status.displayName} records found for ${status.email}`
        : imported > 0
          ? `Imported ${imported} ${status.displayName} ${imported === 1 ? 'activity' : 'activities'}`
          : `${status.displayName} credits are up to date`,
    );
    if (imported > 0) {
      loadCredits();
      setRequirementsRefresh((n) => n + 1);
    }
  });
  // The one-time prompt stays here, where what it imports lands; afterwards the Import credits page is the way back in.
  const importPrompts = creditImports.filter((i) => i.mode === 'prompt' || i.mode === 'imported');

  const licensePresets = buildLicensePresets(licenses ?? []);
  const activeLicensePreset = activePreset?.startsWith('license:')
    ? licensePresets.find((p) => p.key === activePreset)
    : undefined;

  const dateRangeError = endDate < startDate;

  const filtered = dateRangeError
    ? []
    : credits.filter((r) => {
        const d = r.dateEarned.slice(0, 10);
        return d >= startDate && d <= endDate;
      });

  const total = roundTenths(filtered.reduce((s, r) => s + r.creditsEarned, 0));

  const creditsByCategory = filtered.reduce<Record<string, number>>((acc, c) => {
    const key = c.category ?? 'Uncategorized';
    acc[key] = roundTenths((acc[key] ?? 0) + c.creditsEarned);
    return acc;
  }, {});
  const categoryEntries = Object.entries(creditsByCategory).sort((a, b) => b[1] - a[1]);

  // Staleness guard: if the period changed and the selected category has no
  // credits in it anymore, the filter deactivates rather than emptying the table.
  const activeCategoryFilter =
    categoryFilter !== null && creditsByCategory[categoryFilter] !== undefined
      ? categoryFilter
      : null;

  // Category-filter at the credit-entry level (not the activity level) so a
  // mixed-category activity shows only its matching portion and the table's
  // credits agree with the donut slice.
  const visibleRecords = activeCategoryFilter
    ? filtered.filter((r) => (r.category ?? 'Uncategorized') === activeCategoryFilter)
    : filtered;

  const groupedPeriod = groupCreditsForDisplay(filtered);
  const groupedFiltered = activeCategoryFilter
    ? groupCreditsForDisplay(visibleRecords)
    : groupedPeriod;

  // Derived from the unfiltered set so the Actions column doesn't appear and
  // disappear (re-flowing every column) as the user switches period presets.
  const hasExternalCredits = credits.some((r) => r.source === 'External');

  const userName =
    clerkUser?.firstName && clerkUser?.lastName
      ? `${clerkUser.firstName} ${clerkUser.lastName}`
      : clerkUser?.firstName ?? clerkUser?.emailAddresses?.[0]?.emailAddress ?? undefined;

  async function handlePrintReport() {
    setGenerating(true);
    try {
      // The PDF mirrors what the table shows, so an active category filter
      // narrows the export too — and is named in the period context so the
      // document explains its own scope.
      const contextParts = [
        activeLicensePreset ? `${activeLicensePreset.stateLabel} license period` : null,
        activeCategoryFilter ? `${activeCategoryFilter} credits only` : null,
      ].filter((part): part is string => part !== null);
      await generateCpeReport({
        credits: visibleRecords,
        startDate,
        endDate,
        userName,
        periodContext: contextParts.length > 0 ? contextParts.join(' · ') : undefined,
      });
    } catch {
      setPdfSnackOpen(true);
    } finally {
      setGenerating(false);
    }
  }

  function applyPreset(preset: string) {
    // A category slice belongs to one period's breakdown; changing the
    // period invalidates the selection rather than silently re-scoping it.
    setCategoryFilter(null);
    if (preset.startsWith('license:')) {
      const licensePreset = licensePresets.find((p) => p.key === preset);
      if (!licensePreset) return;
      setActivePreset(preset);
      setStartDate(licensePreset.startDate);
      setEndDate(licensePreset.endDate);
      return;
    }
    setActivePreset(preset);
    if (preset === 'year') {
      setStartDate(thisYearStart());
      setEndDate(defaultEnd());
    } else if (preset === 'twoYear') {
      setStartDate(lastYearStart());
      setEndDate(defaultEnd());
    } else if (preset === 'pastTwo') {
      setStartDate(twoYearsAgoStart());
      setEndDate(lastYearEnd());
    } else {
      setStartDate('2000-01-01');
      setEndDate(defaultEnd());
    }
  }

  function handleOpenAdd() {
    setEditingRecord(undefined);
    setDialogOpen(true);
  }

  function handleOpenEdit(record: CreditRecord) {
    setEditingRecord(record);
    setDialogOpen(true);
  }

  async function handleDelete() {
    if (!deleteRecord?.thirdPartyCreditId) return;
    setDeleting(true);
    setDeleteError(false);
    try {
      // The table row (and the confirm dialog) represent the whole activity, which
      // is stored as one credit row per category sharing a creditGroupId — delete
      // the group, not just the first category row.
      if (hasUsableGuid(deleteRecord.creditGroupId)) {
        await cpeHistoryService.deleteThirdPartyCreditGroup(deleteRecord.creditGroupId);
      } else {
        await cpeHistoryService.deleteThirdPartyCredit(deleteRecord.thirdPartyCreditId);
      }
      setSuccessMessage(
        deleteRecord.externalSource
          ? `${externalSourceLabel(deleteRecord.externalSource)} credit deleted`
          : 'External credit deleted',
      );
      setDeleteRecord(undefined);
      loadCredits();
      setRequirementsRefresh((n) => n + 1);
    } catch {
      setDeleteError(true); // keep dialog open, surface the failure
    } finally {
      setDeleting(false);
    }
  }

  async function handleUndoImport() {
    if (!importDone) return;
    setUndoingImport(true);
    try {
      const result = await creditImportService.undo(importDone.id);
      setImportDone(null);
      setSuccessMessage(`Removed ${result.rowsRemoved} imported credit ${result.rowsRemoved === 1 ? 'row' : 'rows'}`);
      loadCredits();
      setRequirementsRefresh((n) => n + 1);
    } catch (error) {
      const body = (error as { response?: { data?: { message?: string } } })?.response?.data;
      setImportUndoError(body?.message || 'Could not undo the import.');
    } finally {
      setUndoingImport(false);
    }
  }

  async function handleCertificateDownload(record: CreditRecord) {
    try {
      await cpeHistoryService.downloadCertificate(record);
    } catch {
      setCertificateSnackOpen(true);
    }
  }

  return (
    <Box>
      {/* Page header */}
      <Typography variant="h4" sx={{ mb: 4 }}>
        CPE Reporting
      </Typography>

      {/* Per-license CPE requirements and progress */}
      <LicenseRequirementsSection refreshToken={requirementsRefresh} onLicensesLoaded={setLicenses} />

      {/* Credit history — generous top margin so the between-section gap is
          clearly larger than any gap within a section */}
      <Box sx={{ mt: 6 }}>
        {/* One-time import prompts (and their post-import follow-up), one per source,
            sit inside the history section, above the header, because what they
            produce lands in this ledger. Afterwards the toolbar buttons are the way back in. */}
        {importPrompts.map((imp) => (
          <CreditImportPanel key={imp.source} import={imp} />
        ))}
        {/* Section header — mirrors the License requirements header (h5 +
            hairline rule) so the two sections read as siblings. The PDF export
            lives down in the period-controls band, not here: it exports the
            filtered range, so it sits beside the dates that define it. */}
        <Box
          sx={{
            mb: 2.5,
            pb: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5">Credit history</Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadCredits}>
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            Failed to load CPE credits. Please check your connection and try again.
          </Alert>
        ) : (
          // One ledger object: summary header, tinted period-controls band,
          // then the entries. A single border wraps all of it so the summary
          // and controls visibly scope the table they filter, like a bank
          // statement: totals up top, transactions below, one document.
          <Card variant="outlined" sx={{ overflow: 'hidden' }}>
            <CreditSummary
              total={total}
              entries={categoryEntries}
              recordCount={groupedPeriod.length}
              categoryFilter={activeCategoryFilter}
              onCategoryToggle={toggleCategoryFilter}
              periodLabel={
                activePreset === 'all'
                  ? 'All time'
                  : activeLicensePreset
                    ? `${activeLicensePreset.stateLabel} · ${formatPeriodRange(startDate, endDate)}`
                    : formatPeriodRange(startDate, endDate)
              }
              controls={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <ToggleButtonGroup
                    value={activePreset}
                    exclusive
                    onChange={(_, value) => { if (value !== null) applyPreset(value); }}
                    size="small"
                    aria-label="Reporting period preset"
                    // Button voice (sentence case, paper surface, tint+dark-step
                    // selection) comes from the theme's MuiToggleButton override.
                    sx={{ flexWrap: 'wrap' }}
                  >
                    <ToggleButton value="year">Current year</ToggleButton>
                    {/* One preset per set-up license (loaded async, appended in
                        API order: primary first). These replace the generic
                        two-year presets, which only return when the user has no
                        tracked licenses (or the fulfillment fetch failed). */}
                    {licensePresets.map((preset) => (
                      <TooltipToggleButton
                        key={preset.key}
                        value={preset.key}
                        tooltip={formatPeriodRange(preset.startDate, preset.endDate)}
                      >
                        {preset.buttonLabel}
                      </TooltipToggleButton>
                    ))}
                    {licenses !== null && licensePresets.length === 0 && [
                      <ToggleButton key="twoYear" value="twoYear">Current two-year period</ToggleButton>,
                      <ToggleButton key="pastTwo" value="pastTwo">Past two years</ToggleButton>,
                    ]}
                    <ToggleButton value="all">All time</ToggleButton>
                  </ToggleButtonGroup>

                  {/* Date fields double as the readout of the active preset's
                      range; editing either one switches to a custom range.
                      No label — the from → to pair explains itself. The export
                      button shares this row, pushed to its far edge: the PDF
                      contains exactly the range these fields define, so they
                      read as one control group. */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    flexWrap: 'wrap',
                  }}
                >
                  <TextField
                    aria-label="Start date"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); setCategoryFilter(null); }}
                    error={dateRangeError}
                    inputProps={{ min: '2000-01-01', max: '2099-12-31' }}
                    sx={{
                      width: 154,
                      '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                      '& .MuiInputBase-input': {
                        fontSize: '0.8125rem',
                        py: 0.625,
                        fontVariantNumeric: 'tabular-nums',
                      },
                    }}
                  />
                  <ArrowForwardIcon
                    aria-hidden
                    sx={{ fontSize: 16, color: 'text.secondary' }}
                  />
                  <TextField
                    aria-label="End date"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); setCategoryFilter(null); }}
                    error={dateRangeError}
                    inputProps={{ min: '2000-01-01', max: '2099-12-31' }}
                    sx={{
                      width: 154,
                      '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                      '& .MuiInputBase-input': {
                        fontSize: '0.8125rem',
                        py: 0.625,
                        fontVariantNumeric: 'tabular-nums',
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <PdfIcon />}
                    onClick={handlePrintReport}
                    disabled={generating || groupedFiltered.length === 0 || dateRangeError}
                    sx={{ ml: 'auto' }}
                  >
                    {generating ? 'Downloading...' : 'Download PDF'}
                  </Button>
                </Box>

                  {dateRangeError && (
                    <Typography variant="caption" color="error">
                      "To" date must be on or after "From" date.
                    </Typography>
                  )}
                </Box>
              }
            />

            {/* Toolbar band directly above the rows: the add affordance stays
                visible however long the ledger grows (a footer would sink below
                the fold), and the removable donut-selection chip shares the
                band when a filter is active — same vocabulary as the admin
                Compliance Report. Hidden on true first run, where the empty
                state carries the action itself. */}
            {credits.length > 0 && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                {activeCategoryFilter && (
                  <Chip
                    size="small"
                    label={`${activeCategoryFilter} only`}
                    onDelete={() => setCategoryFilter(null)}
                    sx={{ bgcolor: 'tint.primaryMuted', color: 'primary.dark', fontWeight: 600 }}
                  />
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAdd}
                  sx={{ ml: 'auto' }}
                >
                  Add external credits
                </Button>
                {/* One entry for every import: connected sources and files alike live behind it. */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => navigate('/app/cpe-history/import')}
                >
                  Import credits
                </Button>
              </Box>
            )}

            {groupedFiltered.length === 0 ? (
              <Box
                sx={{
                  p: 6,
                  textAlign: 'center',
                  bgcolor: 'surface.sunken',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <EmptyLedgerIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {credits.length === 0 ? 'No credits earned yet' : 'No credits in this period'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: 'auto' }}>
                  {credits.length === 0
                    ? 'CPE credits you earn from completed courses and external activities will appear here.'
                    : 'Adjust the reporting period above to see more results.'}
                </Typography>
                {/* External credits are the only kind users create themselves, so the
                    first-run empty state carries the action itself (the toolbar band
                    above is hidden until at least one credit exists). */}
                {credits.length === 0 && (
                  <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAdd}
                    >
                      Add external credits
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      onClick={() => navigate('/app/cpe-history/import')}
                    >
                      Import credits
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              /* No border of its own — the table is the body of the ledger
                 container, separated from the bands above by one hairline */
              <TableContainer
                sx={{ borderTop: '1px solid', borderColor: 'divider', overflowX: 'auto' }}
              >
                <Table sx={{ minWidth: { xs: 400, sm: 'auto' } }}>
                  <TableHead>
                    {/* Header styling (Table Sage bg, uppercase labels) comes from the theme's MuiTableHead override */}
                    <TableRow>
                      <TableCell>Course</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                      <TableCell align="right">Credits</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Category</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Certificate</TableCell>
                      {hasExternalCredits && (
                        <TableCell align="center" sx={{ width: 80 }}>
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedFiltered.map((group) => {
                      const record = group.base;
                      const isExternal = record.source === 'External';
                      const certUrl = record.certificateUrl;
                      return (
                        <TableRow key={group.key} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          {/* Course */}
                          <TableCell>
                            {isExternal ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                  {record.courseTitle}
                                </Typography>
                                {record.providerName && (
                                  <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.25 }}>
                                    {record.providerName}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              // Same color as external titles so the primary scan
                              // column doesn't alternate green/black; link affordance
                              // returns on hover (underline + green).
                              <Link
                                component={RouterLink}
                                to={`/app/courses/${record.courseId}`}
                                variant="body2"
                                underline="hover"
                                sx={{
                                  fontWeight: 500,
                                  color: 'text.primary',
                                  '&:hover': { color: 'primary.dark' },
                                }}
                              >
                                {record.courseTitle}
                              </Link>
                            )}
                          </TableCell>

                          {/* Type */}
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {/* Status-chip contract: tinted bg + deep-step text from status.* tokens.
                                External = neutral (distinct from platform courses without implying
                                doubt about the user's own entries); types use their peer families. */}
                            {isExternal ? (
                              // Imported credits share the neutral external family (same standing
                              // as manual entries); only the label (source name) and glyph differ.
                              <Chip
                                label={record.externalSource ? externalSourceLabel(record.externalSource) : 'External'}
                                size="small"
                                icon={record.externalSource ? <SyncIcon /> : <PublicIcon />}
                                sx={{
                                  bgcolor: 'status.enrolled.bg',
                                  color: 'status.enrolled.fg',
                                  '& .MuiChip-icon': { color: 'inherit' },
                                }}
                              />
                            ) : (
                              <Chip
                                label={record.courseType === 1 ? 'Webinar' : 'On-Demand'}
                                size="small"
                                icon={record.courseType === 1 ? <VideoLibraryIcon /> : <MenuBookIcon />}
                                sx={{
                                  bgcolor: record.courseType === 1 ? 'status.webinar.bg' : 'status.onDemand.bg',
                                  color: record.courseType === 1 ? 'status.webinar.fg' : 'status.onDemand.fg',
                                  '& .MuiChip-icon': { color: 'inherit' },
                                }}
                              />
                            )}
                          </TableCell>

                          {/* Credits — right-aligned tabular numerals so the
                              ledger's numeric column aligns on the decimal */}
                          <TableCell align="right">
                            {/* Dark-Step Rule: green text on light surfaces uses primary.dark for AA contrast */}
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, color: 'primary.dark', fontVariantNumeric: 'tabular-nums' }}
                            >
                              {group.totalCredits.toFixed(1)}
                            </Typography>
                          </TableCell>

                          {/* Category */}
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {group.entries.length > 1 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {group.entries.map((entry) => (
                                  <Typography key={entry.id} variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                                    {entry.creditsEarned.toFixed(1)} · {entry.category ?? 'Uncategorized'}
                                  </Typography>
                                ))}
                              </Box>
                            ) : record.category ? (
                              /* Single-category rows show the name only — the
                                 amount already sits bold in the Credits column */
                              <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                                {record.category}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
                            )}
                          </TableCell>

                          {/* Date — a primary scan key in a chronological ledger,
                              so it keeps full ink and aligned numerals */}
                          <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {formatPeriodDate(record.dateEarned.slice(0, 10), true)}
                          </TableCell>

                          {/* Certificate — icon-only, matching the Edit/Delete
                              vocabulary; a repeated text label per row was pure
                              noise in the table's widest secondary column */}
                          <TableCell align="center">
                            {certUrl ? (
                              <Tooltip title="Download certificate">
                                <IconButton
                                  size="small"
                                  aria-label="Download certificate"
                                  onClick={() => {
                                    void handleCertificateDownload(record);
                                  }}
                                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.dark' } }}
                                >
                                  <DownloadIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
                            )}
                          </TableCell>

                          {/* Actions (only shown when external credits exist) */}
                          {hasExternalCredits && (
                            <TableCell align="center">
                              {isExternal && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                                  {/* Imported credits mirror PICPA's record and are read-only;
                                      delete stays so the user can drop one for good. */}
                                  {!record.externalSource && (
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenEdit(record)}
                                        sx={{ color: 'text.secondary' }}
                                      >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => { setDeleteError(false); setDeleteRecord(record); }}
                                      sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        )}
      </Box>

      {/* Third-party credit add/edit dialog */}
      <ThirdPartyCreditDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          setSuccessMessage(editingRecord ? 'External credit updated' : 'External credit added');
          loadCredits();
          setRequirementsRefresh((n) => n + 1);
        }}
        editRecord={editingRecord}
        categories={categories}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteRecord}
        onClose={() => setDeleteRecord(undefined)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {deleteRecord?.externalSource
            ? `Delete ${externalSourceLabel(deleteRecord.externalSource)} credit`
            : 'Delete external credit'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteRecord?.externalSource
              ? <>Delete "{deleteRecord?.courseTitle}"? It won't be re-imported by future imports.</>
              : <>Are you sure you want to delete "{deleteRecord?.courseTitle}"? This action cannot be undone.</>}
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to delete the credit. Please try again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRecord(undefined)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!importDone}
        autoHideDuration={12000}
        onClose={(_, reason) => { if (reason !== 'clickaway') setImportDone(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setImportDone(null)}
          severity="success"
          sx={{ width: '100%' }}
          action={
            <Button color="inherit" size="small" disabled={undoingImport} onClick={() => void handleUndoImport()}>
              Undo
            </Button>
          }
        >
          Imported {importDone?.rowsImported} {importDone?.rowsImported === 1 ? 'activity' : 'activities'} from your spreadsheet
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!importUndoError}
        autoHideDuration={6000}
        onClose={() => setImportUndoError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setImportUndoError(null)} severity="error" sx={{ width: '100%' }}>
          {importUndoError}
        </Alert>
      </Snackbar>

      <Snackbar
        open={pdfSnackOpen}
        autoHideDuration={5000}
        onClose={() => setPdfSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setPdfSnackOpen(false)} severity="error" sx={{ width: '100%' }}>
          Failed to generate the report. Please try again.
        </Alert>
      </Snackbar>

      <Snackbar
        open={certificateSnackOpen}
        autoHideDuration={5000}
        onClose={() => setCertificateSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCertificateSnackOpen(false)} severity="error" sx={{ width: '100%' }}>
          Failed to download the certificate. Please try again.
        </Alert>
      </Snackbar>
    </Box>
  );
}

/**
 * Donut of the period's credits by category: slices start at 12 o'clock,
 * largest first, stepping the theme's sequential brand-green ramp clockwise.
 * The total sits in the center hole. With no credits it renders a neutral
 * track ring around 0.0.
 */
function CategoryDonut({
  total,
  entries,
  recordCount,
  selected,
  onToggle,
}: {
  total: number;
  entries: [string, number][];
  recordCount: number;
  selected: string | null;
  onToggle: (category: string) => void;
}) {
  const { categorical, tint } = useTheme().palette;
  const SIZE = 148;
  const STROKE = 18;
  const radius = (SIZE - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  // Paper shows through between slices; a single slice renders as an
  // unbroken ring.
  const gap = entries.length > 1 ? 2.5 : 0;
  const fracOf = (amount: number) => (total > 0 ? amount / total : 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative', width: SIZE, height: SIZE }}>
        {/* rotate(-90deg) puts the first slice's start at 12 o'clock */}
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          aria-hidden
          style={{ transform: 'rotate(-90deg)' }}
        >
          {entries.length === 0 && (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={radius}
              fill="none"
              stroke={tint.neutralMuted}
              strokeWidth={STROKE}
            />
          )}
          {entries.map(([category, amount], i) => {
            const arc = Math.max(fracOf(amount) * circumference - gap, 0.75);
            const start = entries
              .slice(0, i)
              .reduce((sum, [, prior]) => sum + fracOf(prior), 0);
            const offset = -(start * circumference) - gap / 2;
            // Slices mirror the legend buttons (which carry the accessible
            // toggle semantics); unselected slices dim so the active one
            // reads as the filter.
            return (
              <circle
                key={category}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={radius}
                fill="none"
                stroke={categorical[i % categorical.length]}
                strokeWidth={STROKE}
                strokeDasharray={`${arc} ${circumference - arc}`}
                strokeDashoffset={offset}
                onClick={() => onToggle(category)}
                style={{
                  cursor: 'pointer',
                  opacity: selected !== null && selected !== category ? 0.3 : 1,
                  transition: 'opacity 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              />
            );
          })}
        </svg>
        {/* Center total — HTML rather than SVG text for crispness and a11y */}
        <Box
          role="group"
          aria-label="Total CPE credits earned"
          aria-live="polite"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.75rem',
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {total.toFixed(1)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2 }}>
            credits
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {recordCount === 1 ? '1 activity' : `${recordCount} activities`}
      </Typography>
    </Box>
  );
}

/**
 * Header region of the credit-history ledger container: scope label, donut,
 * legend, and the tinted period-controls band. Renders no border of its own —
 * the parent Card owns the container so the summary and the table below it
 * read as one document.
 */
function CreditSummary({
  total,
  entries,
  recordCount,
  periodLabel,
  categoryFilter,
  onCategoryToggle,
  controls,
}: {
  total: number;
  entries: [string, number][];
  recordCount: number;
  periodLabel: string;
  categoryFilter: string | null;
  onCategoryToggle: (category: string) => void;
  controls?: ReactNode;
}) {
  const { categorical } = useTheme().palette;
  return (
    <>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Scope label — states which period the number below summarizes */}
        <Typography
          variant="overline"
          component="div"
          sx={{ color: 'text.secondary', lineHeight: 1.5, mb: 1.5 }}
        >
          Credits earned · {periodLabel}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 0 },
          }}
        >
          {/* Donut — total in the center, decomposition around it, so the
              number and its breakdown read as one object */}
          <Box sx={{ pr: { md: 3 }, alignSelf: { xs: 'center', md: 'flex-start' } }}>
            <CategoryDonut
              total={total}
              entries={entries}
              recordCount={recordCount}
              selected={categoryFilter}
              onToggle={onCategoryToggle}
            />
          </Box>

          {/* Vertical divider — desktop only */}
          {entries.length > 0 && (
            <Box
              aria-hidden
              sx={{
                display: { xs: 'none', md: 'block' },
                width: '1px',
                bgcolor: 'divider',
                flexShrink: 0,
                alignSelf: 'stretch',
              }}
            />
          )}

          {/* Breakdown ledger — hugs its content; the controls panel takes
              the remaining card width */}
          {entries.length > 0 && (
            <Box
              sx={{
                pl: { md: 3 },
                pt: { xs: 2, md: 0 },
                borderTop: { xs: '1px solid', md: 'none' },
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 1.75,
              }}
            >
              {/* Ledger — categories with proportional share; swatches match
                  the donut slices (same order, same ramp). Each row is a
                  toggle button that filters the credit table below. */}
              <Box
                role="group"
                aria-label="Credits by category. Select one to filter the table."
                sx={{
                  display: 'grid',
                  // Content-hugging columns (no 1fr): the name column takes its
                  // natural width so % and amounts sit beside the labels
                  // instead of drifting to the card's far edge on wide screens.
                  gridTemplateColumns: 'auto max-content auto auto',
                  justifyContent: 'start',
                  columnGap: 2.5,
                  rowGap: 0.5,
                  alignItems: 'center',
                }}
              >
                {entries.map(([category, amount], i) => {
                  const pct = Math.round((amount / total) * 100);
                  const isSelected = categoryFilter === category;
                  const isDimmed = categoryFilter !== null && !isSelected;
                  return (
                    // Subgrid keeps the four cells aligned across rows while
                    // the whole row acts as one button.
                    <ButtonBase
                      key={category}
                      onClick={() => onCategoryToggle(category)}
                      aria-pressed={isSelected}
                      sx={{
                        display: 'grid',
                        gridColumn: '1 / -1',
                        gridTemplateColumns: 'subgrid',
                        alignItems: 'center',
                        borderRadius: 1,
                        px: 0.75,
                        py: 0.375,
                        mx: -0.75,
                        textAlign: 'left',
                        justifyItems: 'start',
                        opacity: isDimmed ? 0.5 : 1,
                        transition: 'opacity 0.2s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.15s ease-out',
                        bgcolor: isSelected ? 'tint.primaryMuted' : 'transparent',
                        '&:hover': { bgcolor: isSelected ? 'tint.primaryMuted' : 'tint.primarySoft' },
                        '&:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: 1,
                        },
                      }}
                    >
                      <Box
                        aria-hidden
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '2px',
                          bgcolor: categorical[i % categorical.length],
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', lineHeight: 1.4 }}>
                        {category}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontVariantNumeric: 'tabular-nums',
                          textAlign: 'right',
                          justifySelf: 'end',
                          fontFeatureSettings: '"tnum"',
                        }}
                      >
                        {pct}%
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 600,
                          color: 'text.primary',
                          fontVariantNumeric: 'tabular-nums',
                          textAlign: 'right',
                          justifySelf: 'end',
                          minWidth: '3ch',
                        }}
                      >
                        {amount.toFixed(1)}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </CardContent>
      {/* Period controls as a tinted band: the seam between the summary above
          and the table below, scoping both */}
      {controls && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'surface.subtle',
            px: 2.5,
            py: 1.75,
          }}
        >
          {controls}
        </Box>
      )}
    </>
  );
}
